import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {

  static targets = [
    // KPIs
    "kpiAtivos",
    "kpiEntradas",
    "kpiSaidas",
    "detailsModal",
    "detailsContent",
    "kpiAtivosDelta",
    "kpiLiquido",
    "kpiRenovacao",
    "kpiPagantes",
    "kpiPagantesPct",
    "kpiInadimplentes",
    "kpiInadimplentesPct",
    "kpiReceitaMes",
    "kpiMetaMes",
    "kpiMetaBar",
    "kpiMetaPct",

    // Charts
    "chartSocios",
    "chartReceita",
    "chartTicket",
    "chartPlanos",
    "chartEstados",
    "chartBairros",

    // Insights / Risk
    "insightsList",
    "riskLabel",
    "riskBar"
  ]

  static values = {
    url: String,
    detailsUrl: String
  }

  connect() {
    this.charts = {}
    this.load()
    this.detailsShown = () => window.dispatchEvent(new Event("resize"))
    this.detailsModalTarget.addEventListener("shown.bs.modal", this.detailsShown)
    this.detailsModalTarget.addEventListener("shown.bs.tab", this.detailsShown)
  }

  disconnect() {
    this.detailsRequest?.abort()
    this.detailsCharts?.forEach(chart => chart.destroy())
    if (this.hasDetailsModalTarget) {
      this.detailsModalTarget.removeEventListener("shown.bs.modal", this.detailsShown)
      this.detailsModalTarget.removeEventListener("shown.bs.tab", this.detailsShown)
      window.bootstrap.Modal.getInstance(this.detailsModalTarget)?.dispose()
    }
    Object.keys(this.charts).forEach(key => {
      if (this.charts[key]) this.charts[key].destroy()
    })
  }

  async load() {
    const url = new URL(this.urlValue, window.location.origin)

    // Lê o período diretamente da URL da página (definido pelo PeriodFilter via link)
    const currentParams = new URLSearchParams(window.location.search)
    currentParams.forEach((val, key) => {
      url.searchParams.set(key, val)
    })

    // Se nenhum período veio na URL, garante o default "365" (12 meses)
    if (!url.searchParams.has("periodo")) {
      url.searchParams.set("periodo", "365")
    }

    const response = await fetch(url.toString(), {
      headers: { "Accept": "application/json" }
    })

    if (!response.ok) return

    const data = await response.json()

    this.applyKpis(data.kpis)
    if (this.hasKpiEntradasTarget) this.kpiEntradasTarget.textContent = this.n(data.kpis.entradas)
    if (this.hasKpiSaidasTarget) this.kpiSaidasTarget.textContent = this.n(data.kpis.saidas)
    this.applyInsights(data.insights)
    this.applyRisk(data.risk)

    this.renderSocios(data.series.socios)
    this.renderReceita(data.series.receita)
    this.renderTicket(data.series.ticket)
    this.renderPlanos(data.rankings.planos)
    this.renderEstados(data.rankings.estados)
    this.renderBairros(data.rankings.bairros)
  }

  async openDetails(event) {
    const kind = event.currentTarget.dataset.kind
    const url = new URL(this.detailsUrlValue, window.location.origin)
    const params = new URLSearchParams(window.location.search)
    ;["periodo", "data_inicio", "data_fim"].forEach(key => {
      if (params.has(key)) url.searchParams.set(key, params.get(key))
    })
    url.searchParams.set("kind", kind)
    this.detailsRequest?.abort()
    this.detailsRequest = new AbortController()
    this.detailsCharts?.forEach(chart => chart.destroy())
    this.detailsCharts = []
    this.detailsContentTarget.innerHTML = '<div class="modal-header"><h2 class="h5" id="diretoria-details-title">Detalhamento</h2></div><div class="modal-body" role="status">Carregando…</div>'
    window.bootstrap.Modal.getOrCreateInstance(this.detailsModalTarget).show()
    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, signal: this.detailsRequest.signal })
      if (!response.ok) throw new Error("Falha ao carregar detalhamento")
      this.detailsContentTarget.innerHTML = await response.text()
      const source = this.detailsContentTarget.querySelector("[data-details-chart-data]")
      const data = JSON.parse(source.dataset.detailsChartData)
      if (data.plans.length) {
        const chart = new ApexCharts(source.querySelector('[data-details-chart="plans"]'), {
          chart: { type: "donut", height: 360, toolbar: { show: false } },
          series: data.plans.map(row => row.total), labels: data.plans.map(row => row.name),
          legend: { position: "bottom" }
        })
        this.detailsCharts.push(chart)
        chart.render()
      }
      if (data.sellers.length) {
        const chart = new ApexCharts(source.querySelector('[data-details-chart="sellers"]'), {
          chart: { type: "bar", height: Math.max(320, data.sellers.length * 32), toolbar: { show: false } },
          series: [{ name: "Contratos", data: data.sellers.map(row => row.contracts) }],
          xaxis: { categories: data.sellers.map(row => row.name) },
          plotOptions: { bar: { horizontal: true } }
        })
        this.detailsCharts.push(chart)
        chart.render()
      }
    } catch (error) {
      if (error.name === "AbortError") return
      this.detailsContentTarget.innerHTML = '<div class="modal-header"><h2 class="h5" id="diretoria-details-title">Detalhamento</h2></div><div class="modal-body"><p class="text-danger" role="alert">Não foi possível carregar os dados. Feche a janela e tente novamente.</p></div>'
    }
  }

  // =========================================================
  // KPIs
  // =========================================================

  applyKpis(k) {

    if (this.hasKpiAtivosTarget)
      this.kpiAtivosTarget.textContent = this.n(k.ativos)

    if (this.hasKpiAtivosDeltaTarget)
      this.applyDelta(this.kpiAtivosDeltaTarget, k.ativos_delta)

    if (this.hasKpiLiquidoTarget)
      this.kpiLiquidoTarget.textContent = this.signed(k.liquido)

    if (this.hasKpiRenovacaoTarget)
      this.kpiRenovacaoTarget.textContent = this.pct(k.renovacao_pct)

    if (this.hasKpiPagantesTarget)
      this.kpiPagantesTarget.textContent = this.n(k.pagantes)

    if (this.hasKpiPagantesPctTarget)
      this.kpiPagantesPctTarget.textContent = this.pct(k.pagantes_pct)

    if (this.hasKpiInadimplentesTarget)
      this.kpiInadimplentesTarget.textContent = this.n(k.inadimplentes)

    if (this.hasKpiInadimplentesPctTarget)
      this.kpiInadimplentesPctTarget.textContent = this.pct(k.inadimplentes_pct)

    if (this.hasKpiReceitaMesTarget)
      this.kpiReceitaMesTarget.textContent = this.money(k.receita_mes)

    if (this.hasKpiMetaMesTarget)
      this.kpiMetaMesTarget.textContent = this.money(k.meta_mes)

    if (this.hasKpiMetaBarTarget && k.meta_mes > 0) {
      const prog = Math.min(100, (k.receita_mes / k.meta_mes) * 100)
      this.kpiMetaBarTarget.style.width = `${prog.toFixed(0)}%`

      if (this.hasKpiMetaPctTarget) {
        const el = this.kpiMetaPctTarget
        el.textContent = `${prog.toFixed(0)}%`
        el.classList.remove("badge-success", "badge-primary", "badge-warning")

        if (prog >= 100) el.classList.add("badge-success")
        else if (prog >= 85) el.classList.add("badge-primary")
        else el.classList.add("badge-warning")
      }
    }
  }

  applyDelta(el, value) {
    el.classList.remove("kpi-delta--up", "kpi-delta--down", "kpi-delta--flat")

    const abs = Math.abs(value || 0)
    const formatted = `${abs.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`

    if ((value || 0) > 0) {
      el.classList.add("kpi-delta--up")
      el.textContent = `↑ ${formatted}`
    } else if ((value || 0) < 0) {
      el.classList.add("kpi-delta--down")
      el.textContent = `↓ ${formatted}`
    } else {
      el.classList.add("kpi-delta--flat")
      el.textContent = `• ${formatted}`
    }
  }

  // =========================================================
  // Charts
  // =========================================================

  toggleSeries(event) {
    const seriesKey = event.currentTarget.dataset.series
    const seriesName = seriesKey === "meta" ? "Meta" : "Período anterior"
    if (this.charts.socios) {
      this.charts.socios.toggleSeries(seriesName)
      event.currentTarget.classList.toggle("active")
      event.currentTarget.classList.toggle("opacity-50")
    }
  }

  renderSocios(s) {
    if (!this.hasChartSociosTarget) return
    this.destroyChart("socios")

    const options = {
      chart: {
        type: "line",
        height: 360,
        toolbar: { show: false }
      },
      stroke: {
        width: [4, 0, 0, 3, 2],
        curve: "smooth",
        dashArray: [0, 0, 0, 6, 4]
      },
      fill: {
        type: ["gradient", "solid", "solid", "solid", "solid"],
        gradient: {
          opacityFrom: 0.35,
          opacityTo: 0.05
        }
      },
      dataLabels: { enabled: false },
      xaxis: { categories: s.categories },
      series: [
        { name: "Ativos", type: "area", data: s.ativos },
        { name: "Entradas", type: "column", data: s.entradas },
        { name: "Saídas", type: "column", data: s.saidas.map(v => -Math.abs(v)) },
        { name: "Meta", type: "line", data: s.meta },
        { name: "Período anterior", type: "line", data: s.yoy_ativos }
      ]
    }

    this.charts.socios = new ApexCharts(this.chartSociosTarget, options)
    this.charts.socios.render()
  }

  renderReceita(s) {
    if (!this.hasChartReceitaTarget) return
    this.destroyChart("receita")

    const options = {
      chart: { type: "line", height: 320, toolbar: { show: false } },
      stroke: { curve: "smooth", width: 3 },
      xaxis: { categories: s.categories },
      yaxis: {
        labels: {
          formatter: (v) => `R$ ${(v / 1000).toFixed(0)}k`
        }
      },
      tooltip: {
        y: {
          formatter: (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        }
      },
      series: [
        { name: "Período atual", data: s.atual },
        { name: "Período anterior", data: s.yoy }
      ]
    }

    this.charts.receita = new ApexCharts(this.chartReceitaTarget, options)
    this.charts.receita.render()
  }

  renderTicket(s) {
    if (!this.hasChartTicketTarget) return
    this.destroyChart("ticket")

    const options = {
      chart: { height: 320, toolbar: { show: false } },
      xaxis: { categories: s.categories },
      series: [
        { name: "Ticket Médio", type: "line", data: s.ticket_medio },
        { name: "Pagantes", type: "column", data: s.pagantes }
      ]
    }

    this.charts.ticket = new ApexCharts(this.chartTicketTarget, options)
    this.charts.ticket.render()
  }

  renderPlanos(rank) {
    if (!this.hasChartPlanosTarget) return
    this.destroyChart("planos")

    const options = {
      chart: { type: "bar", height: 340, toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, barHeight: "70%" } },
      dataLabels: { enabled: false },
      xaxis: { categories: rank.categories },
      series: [{ name: "Quantidade", data: rank.values }]
    }

    this.charts.planos = new ApexCharts(this.chartPlanosTarget, options)
    this.charts.planos.render()
  }

  renderEstados(rank) {
    if (!this.hasChartEstadosTarget) return
    this.destroyChart("estados")

    const options = {
      chart: { type: "bar", height: 320, toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, barHeight: "70%" } },
      dataLabels: { enabled: false },
      xaxis: { categories: rank.categories },
      series: [{ name: "Sócios", data: rank.values }]
    }

    this.charts.estados = new ApexCharts(this.chartEstadosTarget, options)
    this.charts.estados.render()
  }

  renderBairros(rank) {
    if (!this.hasChartBairrosTarget) return
    this.destroyChart("bairros")

    const options = {
      chart: {
        type: "bar",
        height: 320,
        toolbar: { show: false },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const index = config.dataPointIndex
            if (index !== undefined && index >= 0 && rank.ids && rank.ids[index]) {
              const bairroId = rank.ids[index]
              const targetUrl = `/bairros/${bairroId}`
              if (window.Turbo) {
                window.Turbo.visit(targetUrl)
              } else {
                window.location.href = targetUrl
              }
            }
          }
        }
      },
      plotOptions: { bar: { horizontal: true, barHeight: "70%" } },
      dataLabels: { enabled: false },
      xaxis: { categories: rank.categories },
      series: [{ name: "Sócios", data: rank.values }]
    }

    this.charts.bairros = new ApexCharts(this.chartBairrosTarget, options)
    this.charts.bairros.render()
  }

  destroyChart(key) {
    if (this.charts[key]) {
      this.charts[key].destroy()
      delete this.charts[key]
    }
  }

  // =========================================================
  // Insights / Risk
  // =========================================================

  applyInsights(list) {
    if (!this.hasInsightsListTarget) return
    this.insightsListTarget.innerHTML = ""
    list.slice(0, 4).forEach(text => {
      const li = document.createElement("li")
      li.textContent = text
      this.insightsListTarget.appendChild(li)
    })
  }

  applyRisk(risk) {
    if (this.hasRiskLabelTarget)
      this.riskLabelTarget.textContent = `${risk.label} (${risk.percent}%)`

    if (this.hasRiskBarTarget)
      this.riskBarTarget.style.width = `${risk.percent}%`
  }

  // =========================================================
  // Helpers
  // =========================================================

  n(v) {
    return (v || 0).toLocaleString("pt-BR")
  }

  pct(v) {
    return `${(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`
  }

  money(v) {
    return (v || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    })
  }

  signed(v) {
    return (v >= 0 ? "+" : "") + this.n(v)
  }

  exportPdf() {
    window.dispatchEvent(new Event("resize"))
    setTimeout(() => {
      window.print()
    }, 150)
  }
}